import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PetChangeRequest } from '@/api/petChangeRequests/petChangeRequestsApi.types';
import { Button } from '@/components/ui/button';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import {
  formatPetChangeRequestFieldValue,
  petChangeRequestFieldLabel,
  PET_CHANGE_REQUEST_FIELD_ORDER,
} from '@/utils/petChangeRequestFields';
import { useWorkspacePatientOptionsQuery } from '@/hooks/useWorkspacePatientOptions';
import { RejectPetChangeRequestDialog } from './RejectPetChangeRequestDialog';

interface PetChangeRequestReviewDialogProps {
  request: PetChangeRequest;
  isApproving: boolean;
  isRejecting: boolean;
  onApprove: () => void;
  onReject: (reviewNote: string) => void;
  onClose: () => void;
}

export function PetChangeRequestReviewDialog({
  request,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
  onClose,
}: PetChangeRequestReviewDialogProps) {
  const { t } = useTranslation(['patients', 'common']);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const optionsQuery = useWorkspacePatientOptionsQuery();
  const speciesOptions = optionsQuery.data === undefined ? [] : optionsQuery.data.species;

  const changedFieldNames = PET_CHANGE_REQUEST_FIELD_ORDER.filter(function isChanged(fieldName) {
    return request.proposedChanges[fieldName] !== undefined;
  });

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="w-full max-w-lg bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-6">
        <h2 className="text-lg font-bold text-zooindigo">
          {t('patients:changeRequest.reviewTitle', { patientName: request.patientName })}
        </h2>
        <p className="text-sm text-zoomuted mt-1.5">
          {t('patients:changeRequest.reviewHint', { clientName: request.clientName })}
        </p>

        <table className="w-full mt-4 text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-zoomuted">
              <th className="pb-2 pr-2">{t('patients:changeRequest.fieldColumn')}</th>
              <th className="pb-2 pr-2">{t('patients:changeRequest.currentColumn')}</th>
              <th className="pb-2">{t('patients:changeRequest.suggestedColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {changedFieldNames.map(function renderRow(fieldName) {
              return (
                <tr key={fieldName} className="border-t border-zooborder">
                  <td className="py-2 pr-2 font-semibold text-zooindigo">
                    {petChangeRequestFieldLabel(fieldName, t)}
                  </td>
                  <td className="py-2 pr-2 text-zoomuted">
                    {formatPetChangeRequestFieldValue(
                      fieldName,
                      request.currentValues[fieldName],
                      t,
                      speciesOptions
                    )}
                  </td>
                  <td className="py-2 text-zooprimary font-semibold">
                    {formatPetChangeRequestFieldValue(
                      fieldName,
                      request.proposedChanges[fieldName],
                      t,
                      speciesOptions
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isApproving || isRejecting}
            onClick={onClose}
          >
            {t('common:actions.close')}
          </Button>
          <Button
            type="button"
            variant="destructiveGhost"
            className="flex-1 border border-zooborder"
            disabled={isApproving}
            onClick={function openRejectDialog() {
              setIsRejectDialogOpen(true);
            }}
          >
            {t('patients:changeRequest.reject')}
          </Button>
          <Button type="button" className="flex-1" isLoading={isApproving} onClick={onApprove}>
            {t('patients:changeRequest.approve')}
          </Button>
        </div>
      </div>

      {isRejectDialogOpen === true ? (
        <RejectPetChangeRequestDialog
          petName={request.patientName}
          isSubmitting={isRejecting}
          onConfirm={onReject}
          onCancel={function closeRejectDialog() {
            setIsRejectDialogOpen(false);
          }}
        />
      ) : null}
    </ModalOverlay>
  );
}
